/**
 * PATH: packages/ui/src/kanban/components/KanbanCardPeekActions.tsx
 * FILE_ID: UI-KANBAN-CARD-PEEK-ACTIONS-001
 * PURPOSE: The AI "Next actions" list inside the thread peek, with one-click
 *   "add as checklist step" per item (FE stage 3).
 * WHY:
 *   FEAT-2026-06-20-KANBAN-CARD-AI-THREAD — operator: "emails should be
 *   summarised into action structures … action structure should pop below the
 *   card as the card expands". Stage 2 surfaced the AI action items read-only;
 *   this closes the loop: a single click promotes an AI-detected action into a
 *   real checklist step, which then shows in the card's hover expand-down
 *   checklist. The actual write is injected as `onAddChecklistStep` so this
 *   stays a pure presentational+local-state component (and hosts without the
 *   checklist API simply render the items read-only).
 * FLOW:
 *   ┌──────────────┐  click "+"  ┌─────────────────────┐   ┌──────────────┐
 *   │ AI action    │ ──────────▶ │ onAddChecklistStep  │ → │ per-item ✓   │
 *   │ item row     │  (per item) │ (ensure list + add) │   │ "Added"      │
 *   └──────────────┘             └─────────────────────┘   └──────────────┘
 * DEPENDENCIES:
 *   - lucide-react: kind glyphs + add/spinner/added states.
 *   - ../types CardActionItem / CardActionItemKind.
 * RELATED:
 *   - ./KanbanCardPeek.tsx: renders this; passes items + onAddChecklistStep.
 *   - ./KanbanCardInlineChecklist.tsx: the expand-down list these land in.
 */
import { useState } from 'react'
import {
  Calendar,
  Check,
  CheckSquare,
  Info,
  Loader2,
  Plus,
  Reply,
  Scale,
} from 'lucide-react'

import type { CardActionItem, CardActionItemKind } from '../types'

const KIND_ICON: Record<CardActionItemKind, typeof Reply> = {
  reply: Reply,
  task: CheckSquare,
  schedule: Calendar,
  decision: Scale,
  info: Info,
}

const KIND_TINT: Record<CardActionItemKind, string> = {
  reply: 'text-sky-600 dark:text-sky-400',
  task: 'text-emerald-600 dark:text-emerald-400',
  schedule: 'text-violet-600 dark:text-violet-400',
  decision: 'text-amber-600 dark:text-amber-400',
  info: 'text-neutral-500 dark:text-zinc-400',
}

type AddPhase = 'idle' | 'adding' | 'added' | 'error'

export interface KanbanCardPeekActionsProps {
  items: CardActionItem[]
  /** Promote an action item to a real checklist step. When omitted, items are
   *  read-only (no "add" affordance). Should resolve once the step is saved. */
  onAddChecklistStep?: (text: string) => Promise<void>
  /** Keep the "Add" button visible at rest (touch devices have no hover to
   *  reveal it). Desktop leaves it hover-revealed to stay uncluttered. */
  alwaysShowAdd?: boolean
}

/** The "Next actions" block: AI action items, each with an optional one-click
 *  "add as checklist step". Per-item add state lives here (idle → adding →
 *  added / error) so a slow/failed write only affects its own row. */
export function KanbanCardPeekActions({
  items,
  onAddChecklistStep,
  alwaysShowAdd = false,
}: KanbanCardPeekActionsProps) {
  const [phase, setPhase] = useState<Record<number, AddPhase>>({})

  async function add(idx: number, text: string) {
    if (!onAddChecklistStep) return
    setPhase((p) => ({ ...p, [idx]: 'adding' }))
    try {
      await onAddChecklistStep(text)
      setPhase((p) => ({ ...p, [idx]: 'added' }))
    } catch {
      setPhase((p) => ({ ...p, [idx]: 'error' }))
    }
  }

  return (
    <div>
      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-neutral-400">
        Next actions
      </p>
      <ul className="space-y-1">
        {items.map((item, idx) => {
          const Icon = KIND_ICON[item.kind] ?? CheckSquare
          const st = phase[idx] ?? 'idle'
          const canAdd = Boolean(onAddChecklistStep) && !item.done
          return (
            <li
              key={idx}
              className="group/ai-item flex items-start gap-2 rounded-md px-1.5 py-1 text-[12px] text-neutral-700 hover:bg-neutral-50 dark:text-zinc-200 dark:hover:bg-zinc-800/60"
            >
              <Icon className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${KIND_TINT[item.kind] ?? ''}`} aria-hidden />
              <span className={`min-w-0 flex-1 ${item.done ? 'line-through opacity-60' : ''}`}>
                {item.text}
              </span>
              {canAdd && st === 'added' && (
                <span className="mt-0.5 inline-flex shrink-0 items-center gap-0.5 text-[10px] font-medium text-emerald-600 dark:text-emerald-400">
                  <Check className="h-3 w-3" strokeWidth={3} />
                  Added
                </span>
              )}
              {canAdd && st === 'adding' && (
                <Loader2 className="mt-0.5 h-3.5 w-3.5 shrink-0 animate-spin text-neutral-400" aria-label="Adding" />
              )}
              {canAdd && (st === 'idle' || st === 'error') && (
                <button
                  type="button"
                  title={st === 'error' ? 'Retry — add as checklist step' : 'Add as checklist step'}
                  aria-label={`Add "${item.text}" as a checklist step`}
                  onClick={() => add(idx, item.text)}
                  className={[
                    'mt-px inline-flex shrink-0 items-center gap-0.5 rounded px-1 py-0.5 text-[10px] font-medium transition-all',
                    alwaysShowAdd
                      ? 'opacity-100'
                      : 'opacity-0 group-hover/ai-item:opacity-100 focus:opacity-100',
                    st === 'error'
                      ? 'text-rose-600 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-950/40'
                      : 'text-neutral-500 hover:bg-neutral-200/70 hover:text-neutral-800 dark:text-zinc-400 dark:hover:bg-zinc-700/60',
                  ].join(' ')}
                >
                  <Plus className="h-3 w-3" />
                  {st === 'error' ? 'Retry' : 'Add'}
                </button>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
